import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Target, Check, Pencil, TrendingUp, AlertTriangle, Boxes, ThumbsUp, ThumbsDown, FolderInput, ArrowRight } from "lucide-react";

const rateColor = (r) => (r == null ? "text-slate-400" : r >= 80 ? "text-emerald-600" : r >= 55 ? "text-amber-600" : "text-red-600");
const barColor = (r) => (r == null ? "bg-slate-200" : r >= 80 ? "bg-emerald-500" : r >= 55 ? "bg-amber-500" : "bg-red-500");

// PRIVATE owner/admin AI-accuracy read-out for truck specs (never staff-facing). Mirrors the
// parts pricing tool-accuracy: when the AI drafted a spec, how often did the human KEEP it
// (accepted) vs CHANGE it (corrected)? Advisory only — shows where to trust the AI.
export function TruckAiAccuracyCard() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/truck-ai-accuracy").then(({ data }) => setA(data)).catch(() => setA(false)); }, []);
  if (a === false) return null;

  return (
    <div data-testid="truck-ai-accuracy-card" className="rounded-xl border border-slate-200 bg-white p-4 mt-4">
      <div className="flex items-center gap-2 mb-1"><Target className="w-5 h-5 text-violet-600" /><div className="font-bold">Truck AI accuracy</div><Badge className="bg-slate-100 text-slate-500 text-[10px] ml-1">private · advisory</Badge></div>
      <div className="text-xs text-slate-500 mb-3">When the AI drafted a truck spec, how often you KEPT it (accepted) vs CHANGED it (corrected). Honest signal for where to trust it — never shown to staff.</div>

      {!a || a.total_decisions === 0 ? (
        <div data-testid="accuracy-empty" className="text-xs text-slate-400">No verified AI drafts yet — as you confirm or correct AI-drafted specs, the accuracy picture builds here.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="rounded-lg bg-slate-50 px-2 py-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Overall kept</div>
              <div data-testid="accuracy-overall" className={`font-mono font-bold text-2xl ${rateColor(a.overall_rate_pct)}`}>{a.overall_rate_pct == null ? "—" : `${a.overall_rate_pct}%`}</div>
            </div>
            <div className="rounded-lg bg-emerald-50 px-2 py-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-emerald-600 flex items-center justify-center gap-1"><Check className="w-3 h-3" />Accepted</div>
              <div className="font-mono font-bold text-2xl text-emerald-700">{a.accepted}</div>
            </div>
            <div className="rounded-lg bg-red-50 px-2 py-3 text-center">
              <div className="text-[10px] uppercase tracking-wider text-red-600 flex items-center justify-center gap-1"><Pencil className="w-3 h-3" />Corrected</div>
              <div className="font-mono font-bold text-2xl text-red-700">{a.corrected}</div>
            </div>
          </div>

          {/* AI-01: a 100% must never be read as proof. Until 2026-10-06 this ledger could only
              ever say "accepted" — a correction made after a confirm was not recorded at all — so
              rows from before that are counted here but prove nothing. And a field waved through
              by Confirm-all is weaker evidence than one a person looked at. Both are stated. */}
          {a.trust && (a.trust.rows_before_the_ledger_was_fixed > 0 || a.trust.accepted_by_confirm_all > 0) && (
            <div data-testid="accuracy-trust" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 space-y-1">
              {a.trust.rows_before_the_ledger_was_fixed > 0 && (
                <div data-testid="accuracy-trust-prefix">
                  <b>{a.trust.rows_before_the_ledger_was_fixed}</b> of these decisions were recorded before this
                  record was fixed on 6 Oct 2026. Back then a correction made <i>after</i> a confirm was not
                  written down at all — so those rows could only ever say “accepted”.
                  <b> They are not a measurement.</b> Trust the numbers that build from here.
                </div>
              )}
              {a.trust.accepted_by_confirm_all > 0 && (
                <div data-testid="accuracy-trust-bulk">
                  <b>{a.trust.accepted_by_confirm_all}</b> accepted by “Confirm all”, <b>{a.trust.accepted_one_row_at_a_time}</b> one
                  row at a time. A bulk confirm means nobody necessarily looked at that field.
                </div>
              )}
            </div>
          )}

          {/* by confidence — does the AI's own confidence track reality? */}
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Kept-rate by the AI's confidence</div>
            {["high", "medium", "low"].map((c) => {
              const row = a.by_confidence[c] || { n: 0, rate_pct: null };
              return (
                <div key={c} data-testid={`accuracy-conf-${c}`} className="flex items-center gap-2 text-[12px] py-0.5">
                  <span className="w-16 capitalize text-slate-600">{c}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${barColor(row.rate_pct)}`} style={{ width: `${row.rate_pct || 0}%` }} /></div>
                  <span className={`font-mono w-12 text-right ${rateColor(row.rate_pct)}`}>{row.rate_pct == null ? "—" : `${row.rate_pct}%`}</span>
                  <span className="font-mono text-slate-400 w-10 text-right">{row.n}×</span>
                </div>
              );
            })}
          </div>

          {/* by field — which specs it nails vs fumbles + active coaching */}
          {a.by_field?.length > 0 && (() => {
            const struggling = a.by_field.filter((f) => f.n >= 3 && f.rate_pct != null && f.rate_pct < 60);
            return (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5 flex items-center gap-1"><TrendingUp className="w-3 h-3" />By field (most-verified first)</div>
              {struggling.length > 0 && (
                <div data-testid="accuracy-coaching" className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  <AlertTriangle className="w-3.5 h-3.5 inline text-amber-600 mr-1" />
                  <b>Always double-check:</b> {struggling.map((f) => `${f.label} (kept ${f.rate_pct}%)`).join(", ")} — the AI struggles here.
                </div>
              )}
              <div className="space-y-1">
                {a.by_field.slice(0, 10).map((f) => {
                  const weak = f.n >= 3 && f.rate_pct != null && f.rate_pct < 60;
                  return (
                  <div key={f.key} data-testid={`accuracy-field-${f.key}`} className="flex items-center gap-2 text-[12px]">
                    <span className="w-40 truncate text-slate-600">{f.label}{weak && <span title="AI struggles here — double-check" className="ml-1 text-amber-600">⚠</span>}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${barColor(f.rate_pct)}`} style={{ width: `${f.rate_pct || 0}%` }} /></div>
                    <span className={`font-mono w-12 text-right ${rateColor(f.rate_pct)}`}>{f.rate_pct == null ? "—" : `${f.rate_pct}%`}</span>
                    <span className="font-mono text-slate-400 w-10 text-right">{f.n}×</span>
                  </div>
                  );
                })}
              </div>
            </div>
            );
          })()}

          {/* recent decisions */}
          {a.recent?.length > 0 && (
            <div className="max-h-48 overflow-y-auto border-t border-slate-100 pt-1">
              {a.recent.map((r, i) => (
                <div key={i} data-testid={`accuracy-recent-${i}`} className="flex items-center gap-2 py-1 border-b border-slate-50 text-[11px]">
                  <span className="font-mono text-slate-400 w-20 truncate">{r.stock_number || "—"}</span>
                  <span className="text-slate-600 w-32 truncate">{r.label}</span>
                  {r.action === "accepted"
                    ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]"><Check className="w-3 h-3 mr-0.5" />kept {String(r.ai_value)}</Badge>
                    : <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px]"><Pencil className="w-3 h-3 mr-0.5" />{String(r.ai_value)} → {String(r.human_value)}</Badge>}
                  <span className="font-mono text-slate-300 ml-auto">{r.confidence}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* COMPONENT-GROUPING accuracy — the crew's one-tap live-vehicle validation of AI number grouping */}
      {a && a.grouping && (
        <div data-testid="grouping-accuracy" className="mt-4 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-1"><Boxes className="w-4 h-4 text-violet-600" /><div className="font-semibold text-sm">Component-grouping accuracy</div></div>
          <div className="text-xs text-slate-500 mb-2">When the AI grouped a tag's numbers under a component, did the crew mark it right on the real truck? Your owed live-vehicle validation, captured one tap at a time.</div>
          {a.grouping.total === 0 ? (
            <div data-testid="grouping-empty" className="text-xs text-slate-400">No grouping feedback yet — on the component-spawn screen, the crew taps Right/Wrong and it builds here.</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="rounded-lg bg-slate-50 px-2 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Grouped right</div>
                  <div data-testid="grouping-rate" className={`font-mono font-bold text-2xl ${rateColor(a.grouping.correct_rate_pct)}`}>{a.grouping.correct_rate_pct == null ? "—" : `${a.grouping.correct_rate_pct}%`}</div>
                </div>
                <div className="rounded-lg bg-emerald-50 px-2 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-600 flex items-center justify-center gap-1"><ThumbsUp className="w-3 h-3" />Right</div>
                  <div className="font-mono font-bold text-2xl text-emerald-700">{a.grouping.correct}</div>
                </div>
                <div className="rounded-lg bg-red-50 px-2 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-red-600 flex items-center justify-center gap-1"><ThumbsDown className="w-3 h-3" />Wrong</div>
                  <div className="font-mono font-bold text-2xl text-red-700">{a.grouping.wrong}</div>
                </div>
              </div>
              {a.grouping.by_component?.length > 0 && (
                <div className="mb-3 space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">By component</div>
                  {a.grouping.by_component.map((c) => (
                    <div key={c.component} data-testid={`grouping-comp-${c.component}`} className="flex items-center gap-2 text-[12px]">
                      <span className="w-32 truncate text-slate-600 capitalize">{c.label}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${barColor(c.rate_pct)}`} style={{ width: `${c.rate_pct || 0}%` }} /></div>
                      <span className={`font-mono w-12 text-right ${rateColor(c.rate_pct)}`}>{c.rate_pct == null ? "—" : `${c.rate_pct}%`}</span>
                      <span className="font-mono text-slate-400 w-10 text-right">{c.n}×</span>
                    </div>
                  ))}
                </div>
              )}
              {a.grouping.recent?.length > 0 && (
                <div className="max-h-48 overflow-y-auto border-t border-slate-100 pt-1">
                  {a.grouping.recent.map((r, i) => (
                    <div key={i} data-testid={`grouping-recent-${i}`} className="flex items-start gap-2 py-1 border-b border-slate-50 text-[11px]">
                      <span className="font-mono text-slate-400 w-20 truncate shrink-0">{r.stock_number || "—"}</span>
                      <span className="text-slate-600 w-24 truncate capitalize shrink-0">{r.label}</span>
                      {r.verdict === "correct"
                        ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] shrink-0"><ThumbsUp className="w-3 h-3 mr-0.5" />right</Badge>
                        : <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] shrink-0"><ThumbsDown className="w-3 h-3 mr-0.5" />wrong</Badge>}
                      {r.numbers?.length > 0 && <span className="font-mono text-slate-400 truncate">{r.numbers.map((n) => n.value).join(", ")}</span>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* MIS-FILING patterns — every crew re-file (Move to…) says "AI put it under X, belongs under Y" */}
      {a && a.misfiling && a.misfiling.total > 0 && (() => {
        const repeated = (a.misfiling.patterns || []).filter((p) => p.n >= 2);
        return (
        <div data-testid="misfiling-accuracy" className="mt-4 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-1"><FolderInput className="w-4 h-4 text-violet-600" /><div className="font-semibold text-sm">Where the AI mis-files</div></div>
          <div className="text-xs text-slate-500 mb-2">Every time the crew taps "Move to…" to re-file a serial or spec, it tells you the AI put it under the wrong component. Repeated moves flag the pairs it keeps confusing — free, owner-only.</div>
          {repeated.length > 0 && (
            <div data-testid="misfiling-coaching" className="mb-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 space-y-0.5">
              <div className="flex items-center gap-1 font-semibold"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" />The AI keeps mis-filing these:</div>
              {repeated.map((p, i) => (
                <div key={i} className="flex items-center gap-1 pl-4">
                  <b>{p.from_label}</b><ArrowRight className="w-3 h-3 text-amber-500" /><b>{p.to_label}</b>
                  <span className="text-amber-700">— corrected {p.n}×; double-check this grouping.</span>
                </div>
              ))}
            </div>
          )}
          {a.misfiling.patterns?.length > 0 && (
            <div className="mb-2 space-y-1">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Re-file pairs (most-moved first)</div>
              {a.misfiling.patterns.map((p, i) => (
                <div key={i} data-testid={`misfiling-pair-${i}`} className="flex items-center gap-1.5 text-[12px] py-0.5">
                  <span className="text-slate-600 capitalize truncate">{p.from_label}</span>
                  <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="text-slate-700 font-medium capitalize truncate">{p.to_label}</span>
                  <span className="font-mono text-slate-400 ml-auto shrink-0">{p.n}×</span>
                </div>
              ))}
            </div>
          )}
          {a.misfiling.recent?.length > 0 && (
            <div className="max-h-40 overflow-y-auto border-t border-slate-100 pt-1">
              {a.misfiling.recent.map((r, i) => (
                <div key={i} data-testid={`misfiling-recent-${i}`} className="flex items-center gap-2 py-1 border-b border-slate-50 text-[11px]">
                  <span className="font-mono text-slate-400 w-20 truncate shrink-0">{r.stock_number || "—"}</span>
                  <span className="text-slate-600 truncate">{r.item_label || r.value}</span>
                  <span className="inline-flex items-center gap-1 ml-auto shrink-0 text-slate-500 capitalize"><span>{r.from_label}</span><ArrowRight className="w-3 h-3 text-slate-400" /><b className="text-slate-700">{r.to_label}</b></span>
                </div>
              ))}
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}
